import { FiftyFiftyProps } from 'lib/components/FiftyFifty/FiftyFifty'
import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'
import { TestimonialsProps } from 'lib/components/Testimonials/Testimonials'

import { IconListItem } from '@/components/IconList'
import type { AutoScrollerImage } from '@/components/shipplay/ui_patterns/AutoScroller'

export interface PartnershipBenefitsPageContent {
  hero: NarrativeHeroProps
  fifties: FiftyFiftyProps[]
  partnershipTestimonials: TestimonialsProps
  whatInItForYouHeader: string
  whatInItForYouList: IconListItem[]
  whatDoesItMeanHeader: string
  whatDoesItMeanList: IconListItem[]
}

const BACKGROUND_IMAGE = '/images/shipsticks/partner-background.jpg'

const hero: NarrativeHeroProps = {
  backgroundImage: BACKGROUND_IMAGE,
  header: 'Become a partner',
  subHeader: 'A better experience - for you, your guests, and your team.',
}

const fifties: FiftyFiftyProps[] = [
  {
    image: {
      src: '/images/shipsticks/partner-1.jpg',
    },
    backgroundColor: 'grey',
    header: 'Become a partner',
    layout: 'image-right',
    button: {
      text: 'Become a partner',
      link: {
        href: '#lead-form',
      },
    },
    body: `
            By joining forces with Ship&Play, you're giving your guests a
            better experience right from the start - and giving your team a
            simpler way to support them.`,
    showEyebrow: false,
  },
  {
    image: {
      src: '/images/shipsticks/partner-2.jpg',
    },
    backgroundColor: 'grey',
    header: "You're in good company",
    layout: 'image-left',
    body: `
            We deliver to thousands of hotels, resorts, and destinations around
            the world - from beachfront escapes to legendary courses like Pebble
            Beach. They trust us to deliver exceptional service. You can,
            too.
          `,
    showEyebrow: false,
  },
]

const partnershipTestimonials: TestimonialsProps = {
  title: (
    <>
      What our partners <strong>have&nbsp;to&nbsp;say</strong>
    </>
  ),
  testimonials: [
    {
      stars: 5,
      quote: `Guests can easily ship their clubs and Pebble Beach purchases right
       to their doorstep, making travel to and from the resort truly seamless.
        It's one less thing they have to think about, and it adds to the elevated,
         worry-free experience we aim to deliver`,
      attribution: {
        icon: 'golf',
        name: 'Travis E',
        location: 'Pebble Beach Resorts E-Commerce Manager',
      },
    },
    {
      stars: 5,
      quote: `For many, playing the courses at Pebble Beach Resorts is a
        bucket list venture... Knowing their clubs will be waiting
        for them sets the tone for our guests before they even set foot on property`,
      attribution: {
        icon: 'golf',
        name: 'Lauren G.',
        location: 'The Inn at Spanish Bay Director of Rooms',
      },
    },
  ],
}

const whatInItForYouHeader: string = "What's in it for you?"

const whatInItForYouList: IconListItem[] = [
  {
    icon: 'check-circle-filled',
    header: 'Added value without added work',
    body: (
      <>
        We handle the shipping logistics. You deliver the experience. Our team supports your staff
        every step of the way.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Onsite signage & digital resources',
    body: (
      <>
        From custom landing pages to co-branded materials, we make integration easy and effortless
        for your team.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Revenue opportunities',
    body: (
      <>
        Earn commission on every shipment placed through your channel, along with discounted
        shipping
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Increased guest satisfaction',
    body: (
      <>
        Set the tone for a standout stay by offering a premium, hassle-free shipping option that
        your guests will thank you for.
      </>
    ),
  },
]

const whatDoesItMeanHeader: string = 'What does that mean for your guests?'

const whatDoesItMeanList: IconListItem[] = [
  {
    icon: 'check-circle-filled',
    header: 'Stress-free travel',
    body: (
      <>
        No more lugging bags and gear through airports or squeezing them into rental cars. Guests
        enjoy seamless door-to-door shipping, arriving fresh and ready to play.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Trusted by travelers worldwide',
    body: (
      <>
        With millions of bags shipped and thousands of trips made easier, Ship&Play is the trusted
        name in hands-free travel.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Premium experience',
    body: (
      <>
        Real-time tracking, insurance options, and top-tier customer support mean your guests
        receive 5-star treatment from start to finish.
      </>
    ),
  },
  {
    icon: 'check-circle-filled',
    header: 'Travel light, arrive happy',
    body: (
      <>
        When their bags and gear are already waiting at the destination, the vacation starts sooner
        and lasts longer.
      </>
    ),
  },
]

const content: PartnershipBenefitsPageContent = {
  hero,
  fifties,
  partnershipTestimonials,
  whatInItForYouHeader,
  whatInItForYouList,
  whatDoesItMeanHeader,
  whatDoesItMeanList,
}

export interface ShipplayEmphasizedTitle {
  prefix: string
  emphasis: string
  suffix?: string
}

export interface ShipplayPartnershipHeroContent {
  imgUrl: string
  title: ShipplayEmphasizedTitle
  subheading: React.ReactNode
}

export interface ShipplayPartnershipCalloutBandContent {
  title: ShipplayEmphasizedTitle
  body: React.ReactNode
  cta: {
    label: string
    href: string
  }
}

export interface ShipplayPartnershipFeature {
  title: string
  body: React.ReactNode
}

export interface ShipplayPartnershipFeatureSplitContent {
  imgUrl: string
  imgAlt: string
  imgSide: 'left' | 'right'
  title: ShipplayEmphasizedTitle
  features: ShipplayPartnershipFeature[]
}

export interface ShipplayPartnershipFormContent {
  title: ShipplayEmphasizedTitle
  intro: React.ReactNode
  submitLabel: string
}

export interface ShipplayPartnershipBenefitsContent {
  hero: ShipplayPartnershipHeroContent
  logoShowcase: {
    title: ShipplayEmphasizedTitle
    logos: AutoScrollerImage[]
  }
  calloutBand: ShipplayPartnershipCalloutBandContent
  forYou: ShipplayPartnershipFeatureSplitContent
  forGuests: ShipplayPartnershipFeatureSplitContent
  form: ShipplayPartnershipFormContent
}

// Mirrors playground-shipplay /partnership-benefits (GSE-12008 revision):
// hero → logo showcase → callout band → two feature splits → inquiry form.
// Copy and imagery are the playground's, per the migration ticket.
export const shipplayContent: ShipplayPartnershipBenefitsContent = {
  hero: {
    imgUrl: '/images/shipplay/hero/partner-2.webp',
    title: {
      prefix: 'Become a',
      emphasis: 'Partner',
    },
    subheading: <>Set the new standard for effortless travel&mdash;for you and your guests.</>,
  },
  logoShowcase: {
    title: {
      prefix: 'You’re in',
      emphasis: 'Good Company',
    },
    logos: [
      { imgUrl: '/images/shipplay/logos/virtuoso.webp', imgAlt: 'Virtuoso' },
      { imgUrl: '/images/shipplay/logos/signature-travel-network.webp', imgAlt: 'Signature' },
      {
        imgUrl: '/images/shipplay/logos/ikon-pass.webp',
        imgAlt: 'Ikon Pass',
        imgClassName: 'snplay:h-14',
      },
      { imgUrl: '/images/shipplay/logos/azamara-cruises.webp', imgAlt: 'Azamara Cruises' },
      { imgUrl: '/images/shipplay/logos/clear.webp', imgAlt: 'Clear' },
      { imgUrl: '/images/shipplay/logos/montage.webp', imgAlt: 'Montage' },
      { imgUrl: '/images/shipplay/logos/veer.svg', imgAlt: 'Veer', imgClassName: 'snplay:h-14' },
      { imgUrl: '/images/shipplay/logos/pga-of-america.webp', imgAlt: 'PGA of America' },
    ],
  },
  calloutBand: {
    title: {
      prefix: 'Our partners around the world trust us to deliver exceptional service.',
      emphasis: 'You can, too.',
    },
    body: (
      <>
        By joining forces with Ship&amp;Play, you&rsquo;re giving your guests a more elevated
        experience right from the start&mdash;and giving your team a simpler way to support them.
      </>
    ),
    cta: { label: 'Become a Partner', href: '#lead-form' },
  },
  forYou: {
    imgUrl: '/images/shipplay/partner-1.webp',
    imgAlt: 'Partner and affiliate program ship&play',
    imgSide: 'right',
    title: {
      prefix: 'What’s in it',
      emphasis: 'For You',
      suffix: '?',
    },
    features: [
      {
        title: 'Revenue Opportunities',
        body: (
          <>
            Earn commission on every shipment placed through your channel, along with discounted
            shipping.
          </>
        ),
      },
      {
        title: 'Added Value Without Added Work',
        body: (
          <>
            We handle the shipping. You deliver the experience. Our team supports your staff every
            step of the way.
          </>
        ),
      },
      {
        title: 'Increased Guest Satisfaction',
        body: (
          <>
            Set the tone for a standout stay by offering a premium, door-to-door shipping option
            that your guests will thank you for.
          </>
        ),
      },
      {
        title: 'Onsite Signage & Digital Resources',
        body: (
          <>
            From custom landing pages to co-branded materials, we make integration easy and
            effortless for your team.
          </>
        ),
      },
    ],
  },
  forGuests: {
    imgUrl: '/images/shipplay/partner-2.webp',
    imgAlt: 'Scenic outdoor stock photo used to illustrate the travel partner audience.',
    imgSide: 'left',
    title: {
      prefix: 'How Does it Benefit',
      emphasis: 'Your Guests',
      suffix: '?',
    },
    features: [
      {
        title: 'Premium Experience',
        body: (
          <>
            Real-time tracking, insurance options, and top-tier customer support mean your guests
            receive 5-star treatment from start to finish.
          </>
        ),
      },
      {
        title: 'Stress-free Travel',
        body: (
          <>
            No more schlepping luggage through airports, hoisting bags into overhead bins, or
            squeezing them into rental cars. Guests enjoy seamless door-to-door shipping, arriving
            fresh and ready to relax.
          </>
        ),
      },
      {
        title: 'Trusted Global Network',
        body: (
          <>
            With millions of bags shipped and thousands of trips made easier, Ship&amp;Play is one
            of the most trusted names in travel.
          </>
        ),
      },
      {
        title: 'Travel Light, Arrive Happy',
        body: (
          <>
            When their luggage is already waiting at their destination, the vacation starts sooner
            and lasts longer.
          </>
        ),
      },
    ],
  },
  form: {
    title: {
      prefix: 'Let’s',
      emphasis: 'Talk',
    },
    intro: (
      <>
        Becoming a Ship&amp;Play partner is simple, fast, and completely free. We&rsquo;ll help you
        get set up, support your team, and provide the tools you need to deliver a better experience
        from the moment guests book their trip.
      </>
    ),
    submitLabel: 'Submit',
  },
}

export default content
